// Base API configuration
// In dev-like modes, use relative path for proxy; in production, use full URL from env.
const useProxy = import.meta.env.MODE === 'development' || import.meta.env.MODE === 'ipad';
export const API_BASE_URL = useProxy ? '/api' : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api');
const USE_MOCK_API = import.meta.env.VITE_USE_MOCK_API === 'true';
const USE_ERROR_TESTING = import.meta.env.VITE_USE_ERROR_TESTING === 'true';
const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT || 'development';

import { MockApiService } from './mockApi';
import { errorTestingService } from './errorTestingService';
import { createApiErrorFromResponse, NetworkError, NetworkErrorType } from '@/errors';
import type { AppUpdate } from '@/types';
// Types based on the API schema
export interface Attachment {
  id: string;
  created: string;
  source: 'user_upload' | 'system';
  type: string; // MIME type like 'image/jpeg', 'image/png', etc.
}

export interface Metadata {
  [key: string]: any;
}

export interface CreateMessageRequest {
  chatId: string;
  message: string;
  attachments?: Attachment[];
  metadata?: Metadata | {};
}

// API request format with capitalized properties
export interface ApiCreateMessageRequest {
  Id: string;
  ChatId?: number; // Optional for new chats, using number
  Message: string;
  Attachments?: any[] | null;
  Metadata?: Metadata | null;
}

export interface Message {
  id: string;
  chatId: number;
  text?: string | null; // Text can be null or undefined for AI responses that only have attachments
  status: 'sent' | 'completed' | 'pending' | 'failed';
  created_at: string;
  favourite: boolean;
  type: 'user' | 'ai';
  parent_id: string | null;
  deleted: boolean;
  metadata: Metadata;
  attachments: Attachment[];
}

// The API can return either a single message or an array of messages
export type MessagesResponse = Message | Message[];

export interface AttachmentContent {
  id: string;
  encoded_content: string;
}

export type AttachmentContentsResponse = AttachmentContent[];

export interface Chat {
  id: number;
  name: string;
  isPinned: boolean;
  createdAt: string;
  updatedAt: string;
  deleted: boolean;
}

export interface ApiError {
  message: string;
  code?: string;
  details?: any;
}
// Re-export ApiStatusError for backward compatibility
export { ApiStatusError } from '@/errors';

class ApiClient {
  private readonly baseURL: string;
  private readonly useMock: boolean;
  private readonly useErrorTesting: boolean;

  constructor(baseURL: string, useMock: boolean = false, useErrorTesting: boolean = false) {
    this.baseURL = baseURL;
    this.useMock = useMock;
    this.useErrorTesting = useErrorTesting;
  }

  private async handleErrorResponse(response: Response): Promise<never> {
    // Use the new error handler to create ApiStatusError
    const apiError = await createApiErrorFromResponse(response);
    throw apiError;
  }

  private handleNetworkError(error: unknown): never {
    // Re-throw NetworkError instances as-is
    if (error instanceof NetworkError) {
      throw error;
    }
    
    if (error instanceof TypeError && error.message.includes('fetch')) {
      console.error('🌐 Network error:', error.message);
      throw new NetworkError('Please check your internet connection', NetworkErrorType.CONNECTION);
    } else if (error instanceof Error) {
      console.error('❌ API request failed:', error.message);
      throw error;
    } else {
      console.error('❌ Unknown error:', error);
      throw new Error('An unexpected error occurred');
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    accessToken?: string
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (response.status === 204) {
        return undefined as T;
      }

      // Handle successful responses
      if (response.status === 200 || response.status === 201) {
        const data = await response.json();
        return data;
      }

      // Handle error responses
      await this.handleErrorResponse(response);
      
      // This line should never be reached due to handleErrorResponse throwing
      throw new Error('Unexpected error: response handling failed');

    } catch (error) {
      // Handle network errors, timeouts, etc.
      this.handleNetworkError(error);
    }
  }

  /**
   * Create a new message in the specified chat
   * @param messageData - The message data to create
   * @param accessToken - Optional access token for authentication
   * @returns Promise<MessagesResponse> - Array of messages in the chat
   */
   async createMessage(messageData: ApiCreateMessageRequest, accessToken?: string): Promise<MessagesResponse> {
    if (this.useErrorTesting && errorTestingService) {
      // Use the dedicated error testing service method for consistency
      return errorTestingService.createMessage(messageData);
    }
    
    if (this.useMock) {
      return MockApiService.createMessage(messageData);
    }

    return this.request<MessagesResponse>('/v1/messages', {
      method: 'POST',
      body: JSON.stringify(messageData),
    }, accessToken);
  }

  async getChats(accessToken?: string, pageNumber?: number, pageSize?: number): Promise<Chat[]> {
    if (this.useErrorTesting && errorTestingService) {
      return errorTestingService.getChats();
    }
    
    if (this.useMock) {
      return MockApiService.getChats();
    }

    // Build query string for pagination
    const params = new URLSearchParams();
    if (pageNumber !== undefined) {
      params.append('pageNumber', pageNumber.toString());
    }
    if (pageSize !== undefined) {
      params.append('pageSize', pageSize.toString());
    }
    const queryString = params.toString();
    const endpoint = queryString ? `/v1/chats?${queryString}` : '/v1/chats';

    return this.request<Chat[]>(endpoint, {
      method: 'GET',
    }, accessToken);
  }

  async renameChat(chatId: number, name: string, accessToken?: string): Promise<void> {
    if (this.useErrorTesting && errorTestingService) {
      return errorTestingService.renameChat(chatId, name);
    }

    if (this.useMock) {
      return MockApiService.renameChat(chatId, name);
    }

    return this.request<void>(`/v1/chats/${chatId}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }, accessToken);
  }

 async deleteChat(chatId: number, accessToken?: string): Promise<void> {
  if (this.useErrorTesting && errorTestingService) {
    return errorTestingService.deleteChat(chatId); // return added
  }

  if (this.useMock) {
    return MockApiService.deleteChat(chatId); // return added
  }

  // Real API call
  return this.request<void>(`/v1/delete-chat-history/${chatId}`, {
    method: 'DELETE',
  }, accessToken);
}

  async pinChat(chatId: number, isPinned: boolean, accessToken?: string): Promise<Chat> {
    if (this.useErrorTesting && errorTestingService) {
      return errorTestingService.pinChat(chatId, isPinned);
    }

    if (this.useMock) {
      return MockApiService.pinChat(chatId, isPinned);
    }

    return this.request<Chat>(`/v1/chats/${chatId}/pin`, {
      method: 'PATCH',
      body: JSON.stringify({ isPinned }),
    }, accessToken);
  }


  /**
   * Get messages for a specific chat
   * @param chatId - The ID of the chat to fetch messages for
   * @param accessToken - Optional access token for authentication
   * @returns Promise<Message[]> - Array of messages in the chat
   */
  async getMessages(chatId: number, accessToken?: string): Promise<Message[]> {
    if (this.useErrorTesting && errorTestingService) {
      return errorTestingService.getMessages(chatId);
    }
    
    if (this.useMock) {
      return MockApiService.getMessages(chatId);
    }

    return this.request<Message[]>(`/v1/messages/${chatId}`, {
      method: 'GET',
    }, accessToken);
  }

  async getActiveUpdate(accessToken?: string): Promise<AppUpdate | null> {
    if (this.useErrorTesting && errorTestingService) {
      return errorTestingService.getActiveUpdate();
    }

    if (this.useMock) {
      return MockApiService.getActiveUpdate();
    }

    const result = await this.request<AppUpdate>('/v1/updates/active', {
      method: 'GET',
    }, accessToken);
    return result ?? null;
  }

  async dismissUpdate(id: number, accessToken?: string): Promise<void> {
    if (this.useErrorTesting && errorTestingService) {
      return errorTestingService.dismissUpdate(id);
    }

    if (this.useMock) {
      return MockApiService.dismissUpdate(id);
    }

    return this.request<void>(`/v1/updates/${id}/dismiss`, {
      method: 'POST',
    }, accessToken);
  }

  /**
   * Fetch attachment contents by IDs
   * @param attachmentIds - Array of attachment IDs to fetch
   * @param accessToken - Optional access token for authentication
   * @returns Promise<AttachmentContentsResponse> - Array of attachment contents
   */
  async fetchAttachmentContents(attachmentIds: string[], accessToken?: string): Promise<AttachmentContentsResponse> {
    if (this.useErrorTesting && errorTestingService) {
      return errorTestingService.fetchAttachmentContents(attachmentIds);
    }
    
    if (this.useMock) {
      // Mock implementation for attachment contents
      return attachmentIds.map(id => ({
        id,
        encoded_content: 'mock_base64_content_' + id
      }));
    }

    // Build query string with multiple ids parameters
    const queryParams = attachmentIds.map(id => `ids=${encodeURIComponent(id)}`).join('&');
    const endpoint = `/v1/attachment-contents?${queryParams}`;

    return this.request<AttachmentContentsResponse>(endpoint, {
      method: 'GET',
    }, accessToken);
  }

  /**
   * Resolve a backend-relative path (e.g. "/api/v1/updates/image/x.png") against the API host.
   * The frontend origin only serves the SPA, so API paths must not be resolved against it
   * when the API lives on a different origin.
   */
  private resolveApiUrl(url: string): string {
    if (/^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(url)) return url;
    const pageOrigin = globalThis.location.origin;
    const origin = url.startsWith('/api/') ? new URL(this.baseURL, pageOrigin).origin : pageOrigin;
    return new URL(url, origin).href;
  }

  /**
   * Fetch an image URL with an auth token and return a blob object URL.
   * Use this for images that require authentication (e.g. update preview images).
   * The caller is responsible for calling URL.revokeObjectURL() when done.
   */
  async fetchImageAsObjectUrl(imageUrl: string, accessToken?: string): Promise<string | null> {
    try {
      const headers: Record<string, string> = {};
      if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
      const resolvedUrl = this.resolveApiUrl(imageUrl);
      const response = await fetch(resolvedUrl, { headers });
      if (!response.ok) {
        console.warn(`[api] Image request failed (${response.status}):`, resolvedUrl);
        return null;
      }
      const blob = await response.blob();
      const contentType = (response.headers.get('content-type') || blob.type || '').toLowerCase();
      // Reject only HTML (SPA fallback / error pages); storage backends often serve images as application/octet-stream
      if (contentType.startsWith('text/html')) {
        console.warn(`[api] Image response is HTML (${contentType}):`, resolvedUrl);
        return null;
      }
      return URL.createObjectURL(blob);
    } catch (error) {
      // A cross-origin response without CORS headers surfaces here as a TypeError
      console.warn('[api] Image request failed (network/CORS error):', imageUrl, error);
      return null;
    }
  }

}

export const apiClient = new ApiClient(API_BASE_URL, USE_MOCK_API, USE_ERROR_TESTING);

export const config = {
  baseURL: API_BASE_URL,
  environment: ENVIRONMENT,
  useMock: USE_MOCK_API,
  useErrorTesting: USE_ERROR_TESTING
};
